import { usePersona } from '@festina-life/components'
import { useLocalStorage } from '@festina-life/ui/hooks'
import { type PropsWithChildren, useEffect } from 'react'

import { useAvailableConfigurationPersonas } from '../hooks/useAvailableConfigurationPersonas'
import {
  type ConfigurationPersona,
  isConfigurationPersona,
} from '../uiModels/types'
import { defaultPreferences } from './defaultPreferences'
import { Provider } from './layoutContext'
import type { ViewPreferences } from './types'

const LayoutProvider = ({ children }: PropsWithChildren) => {
  const [preferences, setPreferences] =
    useLocalStorage<ViewPreferences>('preferences')
  const [persona, setPersona] = usePersona()
  const availableConfigurationPersonas = useAvailableConfigurationPersonas()

  useEffect(() => {
    if (
      availableConfigurationPersonas.length > 0 &&
      persona &&
      !availableConfigurationPersonas.includes(persona as ConfigurationPersona)
    ) {
      setPersona(availableConfigurationPersonas[0])
    }
  }, [availableConfigurationPersonas, persona, setPersona])

  return (
    <Provider
      value={{
        preferences: {
          ...defaultPreferences,
          ...preferences,
          templates: {
            ...defaultPreferences.templates,
            ...preferences?.templates,
          },
          parameters: {
            ...defaultPreferences.parameters,
            ...preferences?.parameters,
          },
          common: {
            ...defaultPreferences.common,
            ...preferences?.common,
          },
          tabs: {
            ...defaultPreferences.tabs,
            ...preferences?.tabs,
          },
          glMaps: {
            ...defaultPreferences.glMaps,
            ...preferences?.glMaps,
          },
          reconciliationSpecs: {
            ...defaultPreferences.reconciliationSpecs,
            ...preferences?.reconciliationSpecs,
          },
        },
        onChangeView: setPreferences,
        persona:
          persona && isConfigurationPersona(persona)
            ? persona
            : availableConfigurationPersonas[0]!, // The provider is only rendered if there is at least one available persona, so this is safe to assert.
        onChangePersona: setPersona,
      }}
    >
      {children}
    </Provider>
  )
}

export { LayoutProvider }
